import type { ComponentType, ElementType } from 'react';

import {
  ROUTE_ADMIN,
  ROUTE_BANK_ACCOUNT,
  ROUTE_CRON_JOBS,
  ROUTE_DASHBOARD,
  ROUTE_DEPOSIT,
  ROUTE_KYC,
  ROUTE_ORDERS,
  ROUTE_PRESALE_ORDERS,
  ROUTE_PRESALE_TOKEN,
  ROUTE_PRESALE_USERS,
  ROUTE_REFERRALS,
  ROUTE_SETTINGS,
  ROUTE_SUPPORT,
  ROUTE_TRANSACTIONS,
  ROUTE_USERS,
  ROUTE_WAITING_LIST,
  ROUTE_WITHDRAW,
  ROUTE_WALLETS,
  ROUTE_LOGS,
  ROUTE_NOTIFICATIONS,
  ROUTE_MANAGEMENT,
  ROUTE_MANAGEMENT_ADMINS,
  ROUTE_MANAGEMENT_ROLES,
  // ROUTE_MANAGEMENT_PERMISSIONS,
} from './routes';

export const PERMISSIONS = {
  ALL: 'admin:all',
  USER_READ: 'admin:user:read',
  USER_WRITE: 'admin:user:write',
  TRANSACTION_READ: 'admin:transaction:read',
  TRANSACTION_WRITE: 'admin:transaction:write',
  TRANSACTION_EXECUTE: 'admin:transaction:execute',
  TICKET_READ: 'admin:ticket:read',
  TICKET_WRITE: 'admin:ticket:write',
  KYC_REVIEW: 'admin:kyc:review',
  BANK_ACCOUNT_READ: 'admin:bank_account:read',
  SETTINGS_READ: 'admin:settings:read',
  SETTINGS_WRITE: 'admin:settings:write',
  DASHBOARD_READ: 'admin:dashboard:read',
} as const;

export function hasPermission(granted: string[], required?: string | string[]) {
  if (!required) return true;

  const needed = Array.isArray(required) ? required : [required];

  return needed.every(
    (perm) =>
      granted.includes(PERMISSIONS.ALL) ||
      granted.includes(perm) ||
      granted.some((g) => g.endsWith('*') && perm.startsWith(g.slice(0, -1))),
  );
}

export const ROUTE_PERMISSIONS: Record<string, string> = {
  [ROUTE_DASHBOARD]: PERMISSIONS.DASHBOARD_READ,
  [ROUTE_USERS]: PERMISSIONS.USER_READ,
  [ROUTE_KYC]: PERMISSIONS.KYC_REVIEW,
  [ROUTE_WAITING_LIST]: PERMISSIONS.ALL,
  [ROUTE_PRESALE_ORDERS]: PERMISSIONS.DASHBOARD_READ,
  [ROUTE_PRESALE_USERS]: PERMISSIONS.USER_READ,
  [ROUTE_PRESALE_TOKEN]: PERMISSIONS.SETTINGS_READ,
  [ROUTE_CRON_JOBS]: PERMISSIONS.SETTINGS_READ,
  [ROUTE_BANK_ACCOUNT]: PERMISSIONS.BANK_ACCOUNT_READ,
  [ROUTE_TRANSACTIONS]: PERMISSIONS.TRANSACTION_READ,
  [ROUTE_ORDERS]: PERMISSIONS.DASHBOARD_READ,
  [ROUTE_WALLETS]: PERMISSIONS.TRANSACTION_READ,
  [ROUTE_DEPOSIT]: PERMISSIONS.TRANSACTION_READ,
  [ROUTE_WITHDRAW]: PERMISSIONS.TRANSACTION_READ,
  [ROUTE_REFERRALS]: PERMISSIONS.USER_READ,
  [ROUTE_SUPPORT]: PERMISSIONS.TICKET_READ,
  [ROUTE_SETTINGS]: PERMISSIONS.SETTINGS_READ,
  [ROUTE_LOGS]: PERMISSIONS.DASHBOARD_READ,
  [ROUTE_NOTIFICATIONS]: PERMISSIONS.USER_READ,
  [ROUTE_MANAGEMENT]: PERMISSIONS.ALL,
  [ROUTE_MANAGEMENT_ADMINS]: PERMISSIONS.ALL,
  [ROUTE_MANAGEMENT_ROLES]: PERMISSIONS.ALL,
  // [ROUTE_MANAGEMENT_PERMISSIONS]: PERMISSIONS.ALL,
};

export type SidebarButtonConfig = {
  text: string;
  url: string;
  img?: ComponentType | ElementType;
  permission: string;
};

export type SidebarManagementGroupConfig = {
  text: string;
  permission: string;
  img: ComponentType | ElementType;
  items: SidebarButtonConfig[];
};

export function getRoutePermission(pathname: string): string | undefined {
  const adminPath = pathname.startsWith(ROUTE_ADMIN) ? pathname : `${ROUTE_ADMIN}/${pathname}`;

  const match = Object.entries(ROUTE_PERMISSIONS).find(([route]) => adminPath === route || adminPath.startsWith(`${route}/`));

  return match?.[1];
}

export function getFirstAccessibleRoute(
  permissions: string[],
  flatButtons: SidebarButtonConfig[],
  managementGroup?: SidebarManagementGroupConfig | null,
): string | null {
  const entries = [
    ...flatButtons,
    ...(managementGroup && hasPermission(permissions, managementGroup.permission) ? managementGroup.items : []),
  ];

  const button = entries.find(({ permission }) => hasPermission(permissions, permission));

  if (!button) {
    return null;
  }

  return `${ROUTE_ADMIN}/${button.url}`;
}
